/**
 * Company branding helpers (web). Pro / Pro+ only for custom colour.
 * Firestore: companies/{id}.branding.primaryColor, branding.surfaceColor (page hex), branding.panelColor (optional card/panel hex),
 * branding.sidebarColor (optional hex, Pro/Pro+), branding.logoUrl (Pro/Pro+)
 * Drives Home plan strip + Pattern A app accent + page whitespace (--branding-* on :root).
 */

export const DEFAULT_PLAN_BAR_BG = '#0f172a';

/** Default primary for CTAs / links when no custom brand colour — Crewchain navy. */
export const DEFAULT_APP_PRIMARY = '#0e172a';

/** Default main content / page background (slate-100). */
export const DEFAULT_APP_SURFACE = '#f1f5f9';

/** Panels/cards when no custom page surface and no branding.panelColor (slate-50). */
export const DEFAULT_APP_SURFACE_PANEL = '#f8fafc';

/** Plans that may use custom primary colour on the plan bar */
export const BRANDING_ELIGIBLE_PLAN_IDS = ['pro', 'proplus'];

/** Sidebar logo box — matches Sidebar.jsx defaults before custom branding. */
export const DEFAULT_SIDEBAR_LOGO_MAX_HEIGHT = 56;
export const DEFAULT_SIDEBAR_LOGO_MAX_WIDTH = 200;
export const SIDEBAR_LOGO_MAX_HEIGHT_MIN = 36;
export const SIDEBAR_LOGO_MAX_HEIGHT_MAX = 120;
export const SIDEBAR_LOGO_MAX_WIDTH_MIN = 120;
export const SIDEBAR_LOGO_MAX_WIDTH_MAX = 280;

/** Rounded corners on custom sidebar logo (`companies.branding.logoCornerRadius`). */
export const DEFAULT_LOGO_CORNER_RADIUS = 0;
export const LOGO_CORNER_RADIUS_MAX = 20;
/** Inner padding when `logoBacking` is `white` or `black` (px). */
export const LOGO_TILE_PADDING_PX = 10;

/**
 * @param {object | null | undefined} company
 * @returns {{ cornerRadius: number; backing: 'none' | 'white' | 'black' }}
 */
export function getSidebarLogoFrame(company) {
  const b = company && company.branding;
  const rRaw = b && b.logoCornerRadius;
  const r =
    typeof rRaw === 'number' && !Number.isNaN(rRaw) ? Math.round(rRaw) : DEFAULT_LOGO_CORNER_RADIUS;
  const cornerRadius = Math.min(LOGO_CORNER_RADIUS_MAX, Math.max(0, r));
  const raw = b && b.logoBacking;
  let backing = 'none';
  if (raw === 'white' || raw === 'black') {
    backing = raw;
  } else if (raw === 'panel') {
    backing = 'white';
  }
  return { cornerRadius, backing };
}

/** Horizontal alignment for sidebar logo (`companies.branding.logoAlign`). */
export const DEFAULT_SIDEBAR_LOGO_ALIGN = 'center';

/**
 * @param {object | null | undefined} company
 * @returns {'start' | 'center' | 'end'}
 */
export function getSidebarLogoAlign(company) {
  const v = company && company.branding && company.branding.logoAlign;
  if (v === 'start' || v === 'center' || v === 'end') return v;
  return DEFAULT_SIDEBAR_LOGO_ALIGN;
}

/**
 * Custom company logo URL when uploaded (Pro/Pro+). Non‑Pro / no logo → null (use Crewchain default art).
 * @param {object | null | undefined} company
 * @returns {string | null}
 */
export function getCompanyLogoUrl(company) {
  const u = company && company.branding && company.branding.logoUrl;
  return typeof u === 'string' && u.trim() ? u.trim() : null;
}

/**
 * @param {'start' | 'center' | 'end'} align
 * @returns {'flex-start' | 'center' | 'flex-end'}
 */
export function sidebarLogoAlignToFlexJustify(align) {
  if (align === 'start') return 'flex-start';
  if (align === 'end') return 'flex-end';
  return 'center';
}

/**
 * Safe pixel limits for companies.branding.logoMaxHeight / logoMaxWidth (numbers in Firestore).
 * @param {object | null | undefined} company
 * @returns {{ maxHeight: number; maxWidth: number }}
 */
export function getSidebarLogoDimensions(company) {
  const b = company && company.branding;
  const hRaw = b && b.logoMaxHeight;
  const wRaw = b && b.logoMaxWidth;
  const h =
    typeof hRaw === 'number' && !Number.isNaN(hRaw)
      ? Math.round(hRaw)
      : DEFAULT_SIDEBAR_LOGO_MAX_HEIGHT;
  const w =
    typeof wRaw === 'number' && !Number.isNaN(wRaw)
      ? Math.round(wRaw)
      : DEFAULT_SIDEBAR_LOGO_MAX_WIDTH;
  return {
    maxHeight: Math.min(SIDEBAR_LOGO_MAX_HEIGHT_MAX, Math.max(SIDEBAR_LOGO_MAX_HEIGHT_MIN, h)),
    maxWidth: Math.min(SIDEBAR_LOGO_MAX_WIDTH_MAX, Math.max(SIDEBAR_LOGO_MAX_WIDTH_MIN, w)),
  };
}

/**
 * @param {string} hex "#rrggbb"
 * @returns {{ r: number; g: number; b: number } | null}
 */
export function hexToRgb(hex) {
  const h = hex.replace(/^#/, '');
  if (h.length !== 6) return null;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  if ([r, g, b].some((n) => Number.isNaN(n))) return null;
  return { r, g, b };
}

/**
 * Simple darken for hover states (multiply RGB).
 * @param {string} hex "#rrggbb"
 * @param {number} factor 0–1, default ~0.88
 */
export function darkenHex(hex, factor = 0.88) {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  const clamp = (n) => Math.max(0, Math.min(255, Math.round(n * factor)));
  return `#${[clamp(rgb.r), clamp(rgb.g), clamp(rgb.b)]
    .map((x) => x.toString(16).padStart(2, '0'))
    .join('')}`;
}

/**
 * Linear mix between two #rrggbb colours (t=0 → a, t=1 → b).
 * @param {string} hexA
 * @param {string} hexB
 * @param {number} t 0–1
 */
export function mixHex(hexA, hexB, t) {
  const a = hexToRgb(hexA);
  const b = hexToRgb(hexB);
  if (!a || !b) return hexA;
  const mix = (x, y) => Math.round(x + (y - x) * t);
  return `#${[mix(a.r, b.r), mix(a.g, b.g), mix(a.b, b.b)]
    .map((x) => x.toString(16).padStart(2, '0'))
    .join('')}`;
}

/**
 * @param {unknown} input
 * @returns {string | null} normalized "#rrggbb" or null
 */
export function parseHexColor(input) {
  if (input == null || typeof input !== 'string') return null;
  const s = input.trim();
  const m = s.match(/^#?([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/);
  if (!m) return null;
  let h = m[1];
  if (h.length === 3) {
    h = h.split('').map((c) => c + c).join('');
  }
  return `#${h.toLowerCase()}`;
}

/**
 * Panel colour when branding.panelColor is unset and a custom page surface is set.
 * Linear RGB mix: 62% page surface + 38% white (same formula as resolveCompanyBranding).
 * @param {string | null | undefined} surfaceHex
 * @returns {string}
 */
export function derivePanelColorFromSurface(surfaceHex) {
  const s = parseHexColor(surfaceHex);
  if (s) return mixHex(s, '#ffffff', 0.38);
  return DEFAULT_APP_SURFACE_PANEL;
}

/**
 * Relative luminance 0–1 (sRGB), for contrast heuristics.
 * @param {string} hex "#rrggbb"
 */
export function relativeLuminance(hex) {
  const h = hex.replace(/^#/, '');
  if (h.length !== 6) return 0;
  const rs = parseInt(h.slice(0, 2), 16) / 255;
  const gs = parseInt(h.slice(2, 4), 16) / 255;
  const bs = parseInt(h.slice(4, 6), 16) / 255;
  const lin = (c) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
  const r = lin(rs);
  const g = lin(gs);
  const b = lin(bs);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/**
 * WCAG contrast ratio between two #rrggbb colours (1–21).
 * @param {string} hexFg
 * @param {string} hexBg
 */
export function contrastRatio(hexFg, hexBg) {
  const la = relativeLuminance(hexFg);
  const lb = relativeLuminance(hexBg);
  const L1 = Math.max(la, lb);
  const L2 = Math.min(la, lb);
  return (L1 + 0.05) / (L2 + 0.05);
}

/** Plan strip: pick light vs dark text palette by contrast on bar bg. Uses opacity hierarchy for visual depth. */
function planBarForegroundChoice(planBarBg) {
  const useDarkText = contrastRatio('#0f172a', planBarBg) >= contrastRatio('#ffffff', planBarBg);
  if (useDarkText) {
    return {
      isLightBar: true,
      planBarText: '#0f172a',
      planBarMuted: 'rgba(15,23,42,0.65)',
      planBarSub: 'rgba(15,23,42,0.45)',
    };
  }
  return {
    isLightBar: false,
    planBarText: '#ffffff',
    planBarMuted: 'rgba(255,255,255,0.7)',
    planBarSub: 'rgba(255,255,255,0.5)',
  };
}

/** Convert #rrggbb to HSL (h: 0–360, s: 0–100, l: 0–100). */
function hexToHsl(hex) {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;
  const r = rgb.r / 255, g = rgb.g / 255, b = rgb.b / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0, s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    else if (max === g) h = ((b - r) / d + 2) / 6;
    else h = ((r - g) / d + 4) / 6;
  }
  return { h: h * 360, s: s * 100, l: l * 100 };
}

/** Convert HSL (h: 0–360, s: 0–100, l: 0–100) to #rrggbb. */
function hslToHex(h, s, l) {
  const sn = s / 100, ln = l / 100;
  const a = sn * Math.min(ln, 1 - ln);
  const f = (n) => {
    const k = (n + h / 30) % 12;
    return Math.round(255 * (ln - a * Math.max(-1, Math.min(k - 3, Math.min(9 - k, 1)))))
      .toString(16).padStart(2, '0');
  };
  return `#${f(0)}${f(8)}${f(4)}`;
}

/** Boost a brand primary's HSL lightness until it achieves ≥ 4.5:1 contrast against the dark panel bg (#111111). */
function boostPrimaryForDarkMode(hex) {
  const DARK_BG = '#111111';
  if (contrastRatio(hex, DARK_BG) >= 4.5) return hex;
  const hsl = hexToHsl(hex);
  if (!hsl) return hex;
  let { h, s, l } = hsl;
  for (let i = 0; i < 100; i++) {
    l = Math.min(92, l + 1);
    const candidate = hslToHex(h, s, l);
    if (contrastRatio(candidate, DARK_BG) >= 4.5) return candidate;
    if (l >= 92) break;
  }
  return hslToHex(h, s, 92);
}

/**
 * @param {object | null | undefined} company — Firestore company doc data
 */
export function resolveCompanyBranding(company) {
  const rawPlan = (company && company.planId) || '';
  const planId =
    typeof rawPlan === 'string' ? rawPlan.trim().toLowerCase() : String(rawPlan || '').toLowerCase();
  const eligible = BRANDING_ELIGIBLE_PLAN_IDS.includes(planId);
  const raw = company && company.branding && company.branding.primaryColor;
  const parsed = eligible ? parseHexColor(raw) : null;
  const rawSurface = company && company.branding && company.branding.surfaceColor;
  const surfaceParsed = eligible ? parseHexColor(rawSurface) : null;
  const rawSidebar = company && company.branding && company.branding.sidebarColor;
  const sidebarParsed = eligible ? parseHexColor(rawSidebar) : null;
  const rawPanel = company && company.branding && company.branding.panelColor;
  const panelParsed = eligible ? parseHexColor(rawPanel) : null;

  const planBarBg = parsed || DEFAULT_PLAN_BAR_BG;
  const planFg = planBarForegroundChoice(planBarBg);
  const isLight = planFg.isLightBar;

  /** Page whitespace: main shell + list backgrounds */
  const appSurface = surfaceParsed || DEFAULT_APP_SURFACE;
  /** Cards / tiles / logo preview box: explicit panelColor, else blend custom surface toward white, else default panel */
  const appSurfacePanel =
    panelParsed != null
      ? panelParsed
      : surfaceParsed
        ? mixHex(surfaceParsed, '#ffffff', 0.38)
        : DEFAULT_APP_SURFACE_PANEL;

  /** Readable text on panel/cards: flip to light copy when panel colour is dark (same threshold as primary buttons). */
  const panelLum = relativeLuminance(appSurfacePanel);
  const panelIsLight = panelLum > 0.55;
  const appSurfacePanelFg = panelIsLight ? '#0f172a' : '#f8fafc';
  const appSurfacePanelSub = panelIsLight ? '#475569' : '#cbd5e1';
  const appSurfacePanelMuted = panelIsLight ? '#64748b' : '#94a3b8';
  const appSurfacePanelHint = panelIsLight ? '#94a3b8' : 'rgba(248, 250, 252, 0.55)';
  const appSurfacePanelBorder = panelIsLight ? 'rgba(15, 23, 42, 0.12)' : 'rgba(248, 250, 252, 0.14)';

  /** Sidebar chrome: matches page surface unless branding.sidebarColor is set (Pro/Pro+). */
  const sidebarBg = sidebarParsed != null ? sidebarParsed : appSurface;

  /** App-wide primary (CTAs, links): custom only when Pro/Pro+ + valid saved hex */
  const appPrimary = parsed || DEFAULT_APP_PRIMARY;
  const appPrimaryHover = darkenHex(appPrimary, 0.88);
  const appRgb = hexToRgb(appPrimary);
  const appPrimarySoft = appRgb
    ? `rgba(${appRgb.r}, ${appRgb.g}, ${appRgb.b}, 0.12)`
    : 'rgba(14, 23, 42, 0.12)';
  const appPrimaryRing = appRgb
    ? `rgba(${appRgb.r}, ${appRgb.g}, ${appRgb.b}, 0.28)`
    : 'rgba(14, 23, 42, 0.28)';
  const appPrimaryLum = relativeLuminance(appPrimary);
  const appPrimaryFg = appPrimaryLum > 0.55 ? '#0f172a' : '#ffffff';

  /** Dark-mode variant: lighten primary until it has ≥ 4.5:1 against the dark panel bg. */
  const appPrimaryDarkMode = boostPrimaryForDarkMode(appPrimary);
  const appPrimaryDarkModeHover = darkenHex(appPrimaryDarkMode, 0.88);
  const appPrimaryDarkModeFg =
    contrastRatio('#ffffff', appPrimaryDarkMode) >= contrastRatio('#0f172a', appPrimaryDarkMode)
      ? '#ffffff'
      : '#0f172a';

  /** Vertical rule between sidebar and main: navy by default; matches accent when company has custom colour */
  const DEFAULT_SIDEBAR_BORDER = '#0e172a';
  const sidebarBorder = parsed ? appPrimary : DEFAULT_SIDEBAR_BORDER;

  /** Dark bar: frosted pills so “Manage team” etc. sit on the bar colour (default navy or custom accent), not a fixed second navy. */
  const planBarButtonBg = isLight ? 'rgba(15,23,42,0.08)' : 'rgba(255,255,255,0.14)';
  const planBarButtonBorder = isLight ? 'rgba(15,23,42,0.22)' : 'rgba(255,255,255,0.32)';
  const planBarButtonText = isLight ? '#0f172a' : '#f8fafc';
  const planBarButtonTextMuted = isLight ? '#64748b' : 'rgba(248,250,252,0.55)';

  return {
    planBarBg,
    planBarText: planFg.planBarText,
    planBarMuted: planFg.planBarMuted,
    planBarSub: planFg.planBarSub,
    planBarButtonBg,
    planBarButtonBorder,
    planBarButtonText,
    planBarButtonTextMuted,
    planBarError: isLight ? '#b91c1c' : '#fecaca',
    /** whether custom colour is active (Pro/Pro+ + valid hex) */
    hasCustomPlanBarColor: Boolean(parsed),
    hasCustomSurfaceColor: Boolean(surfaceParsed),
    hasCustomPanelColor: Boolean(panelParsed),
    hasCustomSidebarColor: Boolean(sidebarParsed),
    appSurface,
    appSurfacePanel,
    appSurfacePanelFg,
    appSurfacePanelSub,
    appSurfacePanelMuted,
    appSurfacePanelHint,
    appSurfacePanelBorder,
    appPrimary,
    appPrimaryHover,
    appPrimarySoft,
    appPrimaryRing,
    appPrimaryFg,
    appPrimaryDarkMode,
    appPrimaryDarkModeHover,
    appPrimaryDarkModeFg,
    sidebarBorder,
    sidebarBg,
  };
}

/** Maps resolved tokens to React `style` keys for plan-bar CSS variables (preview + provider). */
export const BRANDING_PLAN_BAR_VAR_KEYS = [
  ['--branding-plan-bar-bg', 'planBarBg'],
  ['--branding-plan-bar-text', 'planBarText'],
  ['--branding-plan-bar-muted', 'planBarMuted'],
  ['--branding-plan-bar-sub', 'planBarSub'],
  ['--branding-plan-bar-button-bg', 'planBarButtonBg'],
  ['--branding-plan-bar-button-border', 'planBarButtonBorder'],
  ['--branding-plan-bar-button-text', 'planBarButtonText'],
  ['--branding-plan-bar-button-text-muted', 'planBarButtonTextMuted'],
  ['--branding-plan-bar-error', 'planBarError'],
];

/** Pattern A: semantic vars for primary actions / links across the app (not only plan bar). */
export const BRANDING_APP_VAR_KEYS = [
  ['--branding-app-surface', 'appSurface'],
  ['--branding-app-surface-panel', 'appSurfacePanel'],
  ['--branding-app-surface-panel-fg', 'appSurfacePanelFg'],
  ['--branding-app-surface-panel-sub', 'appSurfacePanelSub'],
  ['--branding-app-surface-panel-muted', 'appSurfacePanelMuted'],
  ['--branding-app-surface-panel-hint', 'appSurfacePanelHint'],
  ['--branding-app-surface-panel-border', 'appSurfacePanelBorder'],
  ['--branding-app-primary', 'appPrimary'],
  ['--branding-app-primary-hover', 'appPrimaryHover'],
  ['--branding-app-primary-soft', 'appPrimarySoft'],
  ['--branding-app-primary-ring', 'appPrimaryRing'],
  ['--branding-app-primary-fg', 'appPrimaryFg'],
  ['--branding-sidebar-border', 'sidebarBorder'],
  ['--branding-sidebar-bg', 'sidebarBg'],
];

export const BRANDING_ALL_VAR_KEYS = [...BRANDING_PLAN_BAR_VAR_KEYS, ...BRANDING_APP_VAR_KEYS];

/**
 * @param {ReturnType<typeof resolveCompanyBranding>} tokens
 * @returns {Record<string, string>}
 */
export function tokensToCssVarObject(tokens) {
  const out = {};
  if (!tokens) return out;
  for (const [cssVar, key] of BRANDING_ALL_VAR_KEYS) {
    const v = tokens[key];
    if (v != null) out[cssVar] = v;
  }
  return out;
}

/**
 * Preview branding as Pro/Pro+ with draft primary and optional draft page background.
 * @param {string} draftPrimaryHexInput
 * @param {string} [draftSurfaceHexInput] — optional; invalid → default surface in preview
 * @param {string} [draftSidebarHexInput] — omit / undefined → sidebar matches page (no custom sidebarColor).
 * @param {string} [draftPanelHexInput] — omit / undefined → panel derived from page surface (no branding.panelColor).
 */
export function previewBrandingTokens(draftPrimaryHexInput, draftSurfaceHexInput, draftSidebarHexInput, draftPanelHexInput) {
  const primary = parseHexColor(draftPrimaryHexInput);
  const surface = draftSurfaceHexInput !== undefined ? parseHexColor(draftSurfaceHexInput) : null;
  const sidebarCustom = draftSidebarHexInput !== undefined ? parseHexColor(draftSidebarHexInput) : null;
  const panelCustom = draftPanelHexInput !== undefined ? parseHexColor(draftPanelHexInput) : null;
  return resolveCompanyBranding({
    planId: 'pro',
    branding: {
      ...(primary && { primaryColor: primary }),
      ...(surface && { surfaceColor: surface }),
      ...(sidebarCustom && { sidebarColor: sidebarCustom }),
      ...(panelCustom && { panelColor: panelCustom }),
    },
  });
}

/** Preview accent only (invalid primary → core plan defaults for bar). */
export function previewPlanBarTokens(draftHexInput) {
  const parsed = parseHexColor(draftHexInput);
  if (parsed) {
    return resolveCompanyBranding({
      planId: 'pro',
      branding: { primaryColor: parsed },
    });
  }
  return resolveCompanyBranding({ planId: 'core', branding: {} });
}

/**
 * Company branding editor preview: draft accent (invalid hex → default Pro accent) and default
 * page / panel / sidebar — not saved custom surface, panel, or sidebar colours.
 * @param {string} draftPrimaryHexInput
 * @returns {ReturnType<typeof resolveCompanyBranding>}
 */
export function previewBrandingAccentOnly(draftPrimaryHexInput) {
  const primary = parseHexColor(draftPrimaryHexInput);
  return resolveCompanyBranding({
    planId: 'pro',
    branding: primary ? { primaryColor: primary } : {},
  });
}
